import { TestRunStatus } from "./types";

export const subString = (str: string | undefined, length: number) => {
  if (!str) {
    return "";
  }
  if (str.length <= length) {
    return str;
  }
  return `${str.substring(0, length)}...`;
};

export const formatDuration = (
  seconds: number | null | undefined,
  options?: { decimals?: number },
): string => {
  if (seconds === null || seconds === undefined || seconds === 0) return "-";

  const decimals = options?.decimals ?? 0;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  // Format seconds with optional decimals
  if (secs > 0 || parts.length === 0) {
    const formattedSecs = decimals > 0 ? secs.toFixed(decimals) : Math.round(secs).toString();
    parts.push(`${formattedSecs}s`);
  }

  return parts.join(" ");
};

/**
 * Format duration from start and end times
 * @param startTime - ISO timestamp string or Date
 * @param endTime - ISO timestamp string or Date (optional)
 * @param options - Configuration options
 * @param options.showRunning - If true and endTime is null, calculates elapsed time from start to now
 * @returns Formatted duration string or null
 */
export const formatDurationFromTimes = (
  startTime: string | Date | null | undefined,
  endTime?: string | Date | null,
  options?: { showRunning?: boolean },
): string | null => {
  if (!startTime) return null;

  const start = new Date(startTime);

  // If no end time, either return null or calculate elapsed time to now
  if (!endTime) {
    if (!options?.showRunning) return null;

    // Calculate elapsed time from start to now
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const seconds = diff / 1000;
    return formatDuration(seconds);
  }

  // Calculate duration from start to end
  const end = new Date(endTime);
  const diff = end.getTime() - start.getTime();
  const seconds = diff / 1000;

  return formatDuration(seconds);
};

export const maybeDateString = (date?: Date | string | null) => {
  if (!date) {
    return;
  }

  if (typeof date === "string") {
    // Check if it ends in 'Z' or has a timezone offset
    if (date.endsWith("Z")) {
      return new Date(date);
    }

    return new Date(`${date}Z`);
  }

  if (date instanceof Date) {
    return date;
  }
};

export const getRunStatusColor = (status?: TestRunStatus) => {
  if (!status) {
    return "default";
  }

  switch (status) {
    case TestRunStatus.ERROR: {
      return "error";
    }
    case TestRunStatus.FAILED: {
      return "error";
    }
    case TestRunStatus.PASSED: {
      return "success";
    }
    case TestRunStatus.PENDING: {
      return "warning";
    }
    case TestRunStatus.RUNNING: {
      return "info";
    }
    case TestRunStatus.FAILED_PENDING: {
      return "warning";
    }
    case TestRunStatus.FLAKY: {
      return "warning";
    }
    case TestRunStatus.SKIPPED: {
      return "default";
    }
  }
};
